import { useEffect, useMemo, useState } from 'react';
import type { Team } from '@forecastify/shared';
import { Button, Dialog, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';
import { Field } from '../card';
import { SettingsPage } from './settings-page';
import { SettingsTable, type SettingsRow } from './settings-table';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ R-60 · Workspace → Csapatok — the list the workspace keeps.                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The mockup draws no such screen** — it hard-codes `PERSON_TEAMS` — so the project-roles
 * section is the pattern: one table, one dialog for new and rename, and a delete confirm that states
 * what happens. Who is in a team is set on the person, not here.
 *
 * ⚠️ **No colour and no order.** Neither means anything for a team; a picker for either would be a
 * decision with nothing to decide.
 */
export function TeamsSection({
  loading,
  teams,
  canManage,
  serverError,
  onCreate,
  onUpdate,
  onDelete,
}: {
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  readonly teams: readonly Team[];
  readonly canManage: boolean;
  readonly serverError: string | null;
  /** Resolve to whether the save landed; the dialog closes on `true` only. */
  readonly onCreate: (body: { name: string }) => Promise<boolean>;
  readonly onUpdate: (id: string, body: { name: string; version: number }) => Promise<boolean>;
  readonly onDelete: (id: string) => void;
}) {
  const t = useT();
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [editing, setEditing] = useState<Team | 'new' | null>(null);
  const [deleting, setDeleting] = useState<Team | null>(null);
  const [query, setQuery] = useState('');

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle === '' ? teams : teams.filter((one) => one.name.toLowerCase().includes(needle));
  }, [teams, query]);

  const rows: SettingsRow[] = shown.map((team) => ({
    id: team.id,
    onOpen: !canManage
      ? undefined
      : () => {
          setEditing(team);
        },
    cells: [
      { kind: 'name' as const, text: team.name },
      { kind: 'num' as const, text: team.memberCount },
      canManage
        ? {
            kind: 'kebab' as const,
            ariaLabel: t('settings.rowMenu', { name: team.name }),
            items: [
              {
                label: t('common.edit'),
                icon: 'pencil' as const,
                onSelect: () => {
                  setEditing(team);
                },
              },
              { type: 'separator' as const },
              {
                label: t('common.delete'),
                icon: 'trash' as const,
                destructive: true,
                onSelect: () => {
                  setDeleting(team);
                },
              },
            ],
          }
        : { kind: 'blank' as const },
    ],
  }));

  return (
    <div data-screen="settings-teams" className="flex min-h-0 flex-1 flex-col">
      <SettingsPage
        title={t('settings.nav.teams')}
        action={
          !canManage
            ? undefined
            : {
                label: t('settings.teams.new'),
                icon: 'plus',
                onSelect: () => {
                  setEditing('new');
                },
              }
        }
        search={query}
        onSearch={setQuery}
        searchPlaceholder={t('settings.teams.search')}
        error={serverError}
      >
        <SettingsTable
          grid="minmax(0,1fr) 140px 36px"
          minWidth={520}
          columns={[
            { label: t('settings.teams.column.name') },
            { label: t('settings.teams.column.people') },
            {},
          ]}
          groups={[{ rows }]}
          loading={loading}
          empty={{
            icon: 'users',
            title: t('settings.teams.empty'),
            body: t('settings.teams.emptyBody'),
          }}
        />
      </SettingsPage>

      <TeamDialog
        editing={editing}
        busy={saving}
        serverError={saveFailed ? serverError : null}
        onClose={() => {
          setSaveFailed(false);
          setEditing(null);
        }}
        onSubmit={(name) => {
          if (editing === null) return;
          setSaving(true);
          void (
            editing === 'new'
              ? onCreate({ name })
              : onUpdate(editing.id, { name, version: editing.version })
          ).then((landed) => {
            setSaving(false);
            setSaveFailed(!landed);
            if (landed) setEditing(null);
          });
        }}
      />

      {/**
       * ⚠️⚠️ **THE CONFIRM SAYS WHO IT TAKES THE TEAM OFF.** Deleting a team does not refuse when
       * people are in it (`0241` cascades), so the count is the thing being agreed to.
       */}
      <Dialog
        open={deleting !== null}
        onOpenChange={(next) => {
          if (!next) setDeleting(null);
        }}
        title={t('settings.teams.delete')}
        description={
          deleting === null
            ? ''
            : deleting.memberCount === 0
              ? t('settings.teams.deleteUnused', { name: deleting.name })
              : t('settings.teams.deleteUsed', {
                  name: deleting.name,
                  count: deleting.memberCount,
                })
        }
        closeLabel={t('common.close')}
      >
        <div className="mt-1 flex items-center gap-2">
          <div className="flex-1" />
          <Button
            variant="outline"
            size="lg"
            onClick={() => {
              setDeleting(null);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            size="lg"
            onClick={() => {
              if (deleting !== null) onDelete(deleting.id);
              setDeleting(null);
            }}
          >
            {t('common.delete')}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function TeamDialog({
  editing,
  busy,
  serverError,
  onClose,
  onSubmit,
}: {
  readonly editing: Team | 'new' | null;
  readonly busy: boolean;
  readonly serverError: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (name: string) => void;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (editing === null) return;
    setError(null);
    setName(editing === 'new' ? '' : editing.name);
  }, [editing]);

  const submit = (): void => {
    /* ⚠️ The duplicate check is the SERVER's — it is the only one that sees every team. */
    if (name.trim() === '') {
      setError(t('settings.teams.error.name'));
      return;
    }
    onSubmit(name.trim());
  };

  return (
    <Dialog
      open={editing !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t(editing === 'new' ? 'settings.teams.new' : 'settings.teams.edit')}
      closeLabel={t('common.close')}
    >
      <div className="flex flex-col gap-3">
        <Field label={t('settings.teams.name')}>
          <TextInput
            autoFocus
            value={name}
            maxLength={80}
            placeholder={t('settings.teams.namePlaceholder')}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit();
            }}
          />
        </Field>

        {(error ?? serverError) === null ? null : (
          <div className="text-destructive text-[12px]" role="alert">
            {error ?? serverError}
          </div>
        )}

        <div className="mt-1 flex items-center gap-2">
          <div className="flex-1" />
          <Button variant="outline" size="lg" disabled={busy} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="lg" disabled={busy} onClick={submit}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
